import i18n from 'app/services/i18n';

import { getLanguage } from './i18n';

// Local Typings
type DisplayPrice = (price: number, options?: DisplayPriceOptions, localeStringOptions?: LocaleOptions) => string;

interface DisplayPriceOptions {
  displayZeroAsFree?: boolean;
  hideCents?: boolean;
}
interface LocaleOptions {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
}

interface FormatCurrencyOptions {
  abbreviate?: boolean;
  displayZeroAsFree?: boolean;
  hideCents?: boolean;
  hideCurrencySymbol?: boolean;
}

/**
 * @returns a formatted currency string based on the user's locale
 */
export const formatCurrency = (amount: number, currencyTypeName: string, options?: FormatCurrencyOptions) => {
  if (options?.displayZeroAsFree && amount === 0) {
    return i18n.t('util:number.zeroAsFree', 'Free');
  }

  const currency = currencyTypeName || 'USD'; // 'or' handles empty string

  const result = new Intl.NumberFormat(
    getLanguage(), {
      currency,
      currencyDisplay: options?.hideCurrencySymbol ? 'code' : 'narrowSymbol',
      maximumFractionDigits: options?.hideCents ? 0 : 2,
      minimumFractionDigits: options?.hideCents ? 0 : 2,
      style: 'currency',
      ...(options?.abbreviate
        ? {
          compactDisplay: 'short',
          notation: 'compact',
        }
        : {}),
    },
  ).format(amount);

  return options?.hideCurrencySymbol ? result.replace(currency, '').trim() : result;
};

interface FormatNumberOptions {
  abbreviate?: boolean;
}

/**
 * @returns a formatted number string based on the user's locale
 */
export const formatNumber = (amount: number, options?: FormatNumberOptions) =>
  new Intl.NumberFormat(
    getLanguage(),
    {
      ...(options?.abbreviate
        ? {
          compactDisplay: 'short',
          notation: 'compact',
        }
        : {}),
    },
  ).format(amount);

/**
 * @deprecated Use `formatCurrency` instead
 */
export const displayPrice: DisplayPrice = (
  price, options, localeStringOptions = {},
) => {
  const hideCents = !!options && !!options.hideCents;
  const localOptions = {
    maximumFractionDigits: hideCents ? 0 : 2,
    minimumFractionDigits: hideCents ? 0 : 2,
  };
  const formatPrice = (num: number) => {
    try {
      const priceString = Math.abs(num).toLocaleString(undefined,
        { ...localOptions, ...localeStringOptions });

      return num >= 0 ? `$${priceString}` : `-$${priceString}`;
    } catch {
      // There is a known issue in webkit where number formatting does not work if the linked-ICU is old
      // We are seeing this in users on Safari 14 as of 03-18-2022
      // https://trac.webkit.org/changeset/278697/webkit/
      const priceString = hideCents ? Math.floor(Math.abs(num)) : Math.abs(num);

      return num >= 0 ? `$${priceString}` : `-$${priceString}`;
    }
  };
  if (options?.displayZeroAsFree && price === 0) {
    return 'Free';
  }

  return formatPrice(price);
};

export const convertCentsToDollars = (priceInCents: number) => priceInCents / 100;

export const roundToHundredth = (num: number) => Math.round(num * 100) / 100;

/**
 * @deprecated Use `formatNumber` instead
 */
export function abbreviateNumber({
  locale = getLanguage(),
  number,
}: {
  locale?: string;
  number: number;
}): string {
  return new Intl.NumberFormat(locale,
    {
      compactDisplay: 'short',
      notation: 'compact',
    } as Intl.NumberFormatOptions).format(number);
}
